'use client';

import React, { useState, useEffect, useRef } from 'react';
import { Check, ChevronDown } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

const Combobox = ({
  selected,
  onChange,
  options = [],
  placeholder = 'Pasirinkite',
  allowNullOption = true,
  className = '',
  label = '',
}) => {
  const [open, setOpen] = React.useState(false);

  const handleSelect = (value) => {
    const selectedOption = options.find((opt) => opt.value === value) || null;
    onChange(selectedOption);
    setOpen(false);
  };

  const triggerRef = useRef(null);
  const [triggerWidth, setTriggerWidth] = useState(0);

  // Update the trigger width whenever the button or popover opens
  useEffect(() => {
    if (triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth);
    }
  }, [open, selected]);

  return (
    <div className={cn('relative', className)}>
      {label && <span className="block mb-1 text-sm font-medium">{label}</span>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div ref={triggerRef} className="w-full h-full">
            <Button
              type="button"
              variant="fullWidth"
              role="combobox"
              aria-expanded={open}
              className={cn(
                'flex items-center justify-between overflow-hidden text-sm text-left bg-white border text-black transition-all',
                open
                  ? 'rounded-t-[1.4rem] rounded-b-none border-b-gray-300'
                  : 'rounded-full',
              )}
              onClick={() => setOpen(!open)}
            >
              <span className="truncate flex-1 min-w-0 block">
                {selected?.label || placeholder}
              </span>
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </div>
        </PopoverTrigger>

        <PopoverContent
          align="start"
          sideOffset={0}
          style={{ width: triggerWidth }} // Dynamically set the width
          className="max-w-full rounded-b-[1.4rem] border border-t border-white border-b-gray-300 bg-white shadow-md max-h-60 overflow-y-auto p-0"
        >
          <Command>
            <CommandInput
              placeholder="Ieškoti..."
              className="px-4 py-2 border-b text-sm focus:outline-none"
            />
            <CommandList className="max-h-full overflow-visible">
              <CommandEmpty>
                <div className="px-4 py-2 text-sm text-gray-500">
                  Nerasta jokių variantų.
                </div>
              </CommandEmpty>
              <CommandGroup>
                {allowNullOption && (
                  <CommandItem
                    key="null-option"
                    value=""
                    onSelect={() => handleSelect(null)}
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm whitespace-normal break-words"
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        selected === null ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    {placeholder}
                  </CommandItem>
                )}
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.label}
                    onSelect={() => handleSelect(option.value)}
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm whitespace-normal break-words"
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        selected?.value === option.value
                          ? 'opacity-100'
                          : 'opacity-0',
                      )}
                    />
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default Combobox;
